import { useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { ModalBase } from '@components/Modal';
import { Input } from '@components/Input';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { micrositesService } from '@services';
import DomainDetails from './DomainDetails';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        maxWidth: 520,
        width: 'calc(100% - 32px)',
    },
    '& .modal-base-content': {
        padding: '0 24px',
    },
});

const Content = styled('div')({
    padding: '16px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
});

const HOSTNAME_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

function normalizeHostname(value) {
    let h = (value || '').trim().toLowerCase();
    if (h.startsWith('https://')) h = h.slice(8);
    if (h.startsWith('http://')) h = h.slice(7);
    const slashIdx = h.indexOf('/');
    if (slashIdx >= 0) h = h.slice(0, slashIdx);
    const colonIdx = h.indexOf(':');
    if (colonIdx >= 0) h = h.slice(0, colonIdx);
    return h;
}

function validateHostname(value, t) {
    const h = normalizeHostname(value);
    if (!h) return t('MicrositesPage.domainRequired');
    if (h.length < 4 || h.length > 253) return t('MicrositesPage.domainLengthError');
    if (!h.includes('.')) return t('MicrositesPage.domainInvalidFormat');
    if (!HOSTNAME_REGEX.test(h)) return t('MicrositesPage.domainInvalidFormat');
    if (h.endsWith('.pluspoint.io') || h.endsWith('.plsp.io') || h === 'pluspoint.io' || h === 'plsp.io')
        return t('MicrositesPage.domainPluspoint');
    return '';
}

const AddDomainModal = ({ open, onClose, onDomainAdded }) => {
    const { t } = useTranslation();
    const [hostname, setHostname] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [createdDomain, setCreatedDomain] = useState(null);

    const reset = useCallback(() => {
        setHostname('');
        setError('');
        setLoading(false);
        setCreatedDomain(null);
    }, []);

    const handleClose = useCallback(() => {
        if (createdDomain) onDomainAdded(createdDomain);
        reset();
        onClose();
    }, [createdDomain, onDomainAdded, reset, onClose]);

    const handleChange = e => {
        setHostname(e.target.value);
        if (error) setError('');
    };

    const handleAdd = async () => {
        const validationError = validateHostname(hostname, t);
        if (validationError) {
            setError(validationError);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const domain = await micrositesService.addCustomDomain(normalizeHostname(hostname));
            setCreatedDomain(domain);
        } catch (err) {
            setError(err?.message || t('MicrositesPage.addDomainError'));
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Show domain details (same view as Edit Domain)
    if (createdDomain) {
        return (
            <Modal
                isOpen={open}
                aboveModal
                handleClose={handleClose}
                title={t('MicrositesPage.editDomain', { hostname: createdDomain.hostname })}
                onPrimaryAction={handleClose}
                primaryActionText={t('buttons.done')}
            >
                <DomainDetails domain={createdDomain} onDomainUpdated={setCreatedDomain} />
            </Modal>
        );
    }

    // Step 1: Hostname input
    return (
        <Modal
            isOpen={open}
            aboveModal
            handleClose={handleClose}
            title={t('MicrositesPage.addCustomDomain')}
            onPrimaryAction={handleAdd}
            primaryActionText={loading ? '' : t('MicrositesPage.addDomain')}
            primaryActionDisabled={loading || !hostname.trim()}
            subtitle={loading ? <CircularProgress size={20} /> : null}
        >
            <Content>
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                    {t('MicrositesPage.enterDomainDescription')}
                </Typography>
                <Input
                    inputProps={{
                        value: hostname,
                        onChange: handleChange,
                        placeholder: 'links.yourdomain.com',
                        onKeyDown: e => {
                            if (e.key === 'Enter' && hostname.trim() && !loading) handleAdd();
                        },
                    }}
                    label={t('MicrositesPage.domain')}
                    fullWidth
                    error={!!error}
                    helperText={error || ''}
                    disabled={loading}
                />
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                    {t('MicrositesPage.domainOwnershipNote')}
                </Typography>
            </Content>
        </Modal>
    );
};

export default AddDomainModal;
